import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { Alert, Box, Button, Skeleton, Typography, useTheme } from '@mui/material';
import React from 'react';
import { useTranslation } from 'react-i18next';

// Add TypeScript declaration for the Beacon API
declare global {
    interface Window {
        Beacon?: (method: string, ...params: any[]) => void;
    }
}

interface TransactionStatusBarProps {
    onAction?: () => void;
    actionLabel?: string;
    primaryText?: string;
    secondaryText?: string;
    showActionButton?: boolean;
    transactionCount?: number;
    warningCount?: number;
    totalCount?: number;
    isLoading?: boolean;
}

const TransactionStatusBar: React.FC<TransactionStatusBarProps> = ({
    onAction,
    actionLabel,
    showActionButton = false,
    warningCount = 0,
    totalCount = 0,
    isLoading = false,
}) => {
    const { t } = useTranslation();
    const theme = useTheme();
    const isDarkMode = theme.palette.mode === 'dark';

    // Dark mode uses a darker background with bright text
    // Light mode uses a light blue background with dark blue text
    const styles = {
        backgroundColor: isDarkMode ? '#1e3a5f' : '#e3f2fd',
        textPrimary: isDarkMode ? '#ffffff' : theme.palette.text.primary,
        iconColor: isDarkMode ? '#bbdefb' : '#2196f3',
        buttonBg: isDarkMode ? '#2196f3' : '#1976d2',
        buttonHover: isDarkMode ? '#1976d2' : '#0d47a1',
        progressBgColor: isDarkMode ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)',
        progressBarColor: isDarkMode ? '#64b5f6' : '#1976d2',
    };

    // Don't render if no warnings or no transactions (but still show loading state)
    if ((warningCount === 0 || totalCount === 0) || isLoading) {
        return null;
    }

    return (
        <Box sx={{ width: '100%' }}>
            <Alert
                severity="info"
                icon={<InfoOutlinedIcon />}
                sx={{
                    backgroundColor: styles.backgroundColor,
                    color: styles.textPrimary,
                    minHeight: 54,
                    height: 'auto',
                    padding: 0,
                    '.MuiAlert-icon': {
                        color: styles.iconColor,
                        marginRight: 1,
                        padding: '12px 0',
                        paddingLeft: 2,
                        alignItems: 'center',
                    },
                    '.MuiAlert-message': {
                        padding: '12px 0',
                        width: '100%',
                        display: 'flex',
                        alignItems: 'center',
                    },
                    '.MuiAlert-action': {
                        padding: { xs: '0 0 12px 12px', sm: '12px 0' },
                        paddingRight: { xs: 1, sm: 2 },
                        alignItems: 'center',
                        marginTop: { xs: '8px', sm: 0 },
                        alignSelf: 'flex-start',
                        width: { xs: '100%', sm: 'auto' },
                    },
                    display: 'flex',
                    flexDirection: { xs: 'column', sm: 'row' },
                    alignItems: { xs: 'stretch', sm: 'center' },
                }}
                action={
                    showActionButton && onAction ? (
                        <Button
                            variant="contained"
                            onClick={onAction}
                            sx={{
                                backgroundColor: styles.buttonBg,
                                color: 'white',
                                fontWeight: 'bold',
                                fontSize: '1.1rem',
                                textTransform: 'none',
                                px: 2,
                                py: 1,
                                mt: { xs: 1, sm: 0 },
                                width: { xs: '100%', sm: 'auto' },
                                '&:hover': {
                                    backgroundColor: styles.buttonHover,
                                },
                            }}
                        >
                            {actionLabel || t('common.action')}
                        </Button>
                    ) : null
                }
            >
                {isLoading ? (
                    <Box sx={{ width: '100%', display: 'flex', alignItems: 'center', px: 1 }}>
                        <Skeleton
                            variant="rectangular"
                            width="33%"
                            height={12}
                            sx={{
                                borderRadius: 6,
                                bgcolor: isDarkMode ? 'rgba(255, 255, 255, 0.1)' : 'rgba(255, 255, 255, 0.2)',
                            }}
                        />
                        <Skeleton
                            variant="text"
                            width={200}
                            sx={{
                                ml: 2,
                                bgcolor: isDarkMode ? 'rgba(255, 255, 255, 0.1)' : 'rgba(255, 255, 255, 0.2)',
                            }}
                        />
                    </Box>
                ) : (
                    <Box
                        sx={{
                            width: '100%',
                            px: 1,
                        }}
                    >
                        {/* Mobile view - completely stacked elements */}
                        <Box
                            sx={{
                                display: { xs: 'flex', md: 'none' },
                                flexDirection: 'column',
                                gap: 1.5,
                            }}
                        >
                            {/* Progress bar - full width on mobile */}
                            <Box
                                sx={{
                                    width: '100%',
                                    height: 12,
                                    bgcolor: isDarkMode ? 'rgba(255, 255, 255, 0.1)' : 'rgba(255, 255, 255, 0.5)',
                                    borderRadius: 6,
                                    overflow: 'hidden',
                                    position: 'relative',
                                    display: 'flex',
                                }}
                            >
                                {/* Good transactions (light blue) */}
                                <Box
                                    sx={{
                                        width: `${Math.max(0, 100 - (warningCount / totalCount) * 100)}%`,
                                        bgcolor: isDarkMode ? '#64b5f6' : '#64b5f6', // Light blue
                                        height: '100%',
                                        transition: 'width 0.3s ease-in-out',
                                    }}
                                />

                                {/* Warning transactions (same color as good transactions but with white stripes) */}
                                <Box
                                    sx={{
                                        width: `${(warningCount / totalCount) * 100}%`,
                                        height: '100%',
                                        transition: 'width 0.3s ease-in-out',
                                        backgroundColor: isDarkMode ? '#64b5f6' : '#64b5f6', // Same as good transactions
                                        backgroundSize: '6px 6px',
                                        backgroundImage: `linear-gradient(
                                        -45deg,
                                        #ffffff 25%,
                                        transparent 25%,
                                        transparent 50%,
                                        #ffffff 50%,
                                        #ffffff 75%,
                                        transparent 75%,
                                        transparent
                                    )`,
                                    }}
                                />
                            </Box>

                            {/* Message text */}
                            <Typography
                                variant="body2"
                                sx={{
                                    fontWeight: 'medium',
                                    fontSize: '0.9rem',
                                    color: styles.textPrimary,
                                }}
                            >
                                {t('transactions.statusBar.needReview', { warningCount, totalCount })}
                            </Typography>

                            {/* Help link */}
                            <Typography
                                variant="body2"
                                onClick={() => {
                                    if (window.Beacon) {
                                        window.Beacon('open');
                                        window.Beacon('search', 'warnungen');
                                    }
                                }}
                                sx={{
                                    color: styles.textPrimary,
                                    textDecoration: 'underline',
                                    cursor: 'pointer',
                                    fontSize: '0.85rem',
                                    '&:hover': {
                                        color: isDarkMode ? '#e3f2fd' : theme.palette.primary.main,
                                    },
                                }}
                            >
                                {t('transactions.statusBar.howToFix')}
                            </Typography>
                        </Box>

                        {/* Desktop view - horizontal layout */}
                        <Box
                            sx={{
                                display: { xs: 'none', md: 'flex' },
                                alignItems: 'center',
                                gap: 2,
                            }}
                        >
                            {/* Progress bar - limited to 20% width */}
                            <Box
                                sx={{
                                    width: '20%',
                                    minWidth: '60px',
                                    height: 12,
                                    bgcolor: isDarkMode ? 'rgba(255, 255, 255, 0.1)' : 'rgba(255, 255, 255, 0.5)',
                                    borderRadius: 6,
                                    overflow: 'hidden',
                                    position: 'relative',
                                    display: 'flex',
                                    flexShrink: 0,
                                }}
                            >
                                {/* Good transactions (light blue) */}
                                <Box
                                    sx={{
                                        width: `${Math.max(0, 100 - (warningCount / totalCount) * 100)}%`,
                                        bgcolor: isDarkMode ? '#64b5f6' : '#64b5f6', // Light blue
                                        height: '100%',
                                        transition: 'width 0.3s ease-in-out',
                                    }}
                                />

                                {/* Warning transactions (same color as good transactions but with white stripes) */}
                                <Box
                                    sx={{
                                        width: `${(warningCount / totalCount) * 100}%`,
                                        height: '100%',
                                        transition: 'width 0.3s ease-in-out',
                                        backgroundColor: isDarkMode ? '#64b5f6' : '#64b5f6', // Same as good transactions
                                        backgroundSize: '6px 6px',
                                        backgroundImage: `linear-gradient(
                                        -45deg,
                                        #ffffff 25%,
                                        transparent 25%,
                                        transparent 50%,
                                        #ffffff 50%,
                                        #ffffff 75%,
                                        transparent 75%,
                                        transparent
                                    )`,
                                    }}
                                />
                            </Box>

                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                <Typography
                                    variant="body2"
                                    sx={{
                                        fontWeight: 'medium',
                                        fontSize: '0.9rem',
                                        color: styles.textPrimary,
                                    }}
                                >
                                    {t('transactions.statusBar.needReview', { warningCount, totalCount })}
                                </Typography>

                                <Typography
                                    variant="body2"
                                    onClick={() => {
                                        if (window.Beacon) {
                                            window.Beacon('open');
                                            window.Beacon('search', 'warnungen');
                                        }
                                    }}
                                    sx={{
                                        color: styles.textPrimary,
                                        textDecoration: 'underline',
                                        cursor: 'pointer',
                                        fontSize: '0.85rem',
                                        ml: 2,
                                        '&:hover': {
                                            color: isDarkMode ? '#e3f2fd' : theme.palette.primary.main,
                                        },
                                    }}
                                >
                                    {t('transactions.statusBar.howToFix')}
                                </Typography>
                            </Box>
                        </Box>
                    </Box>
                )}
            </Alert>
        </Box>
    );
};

export default TransactionStatusBar;
